import { useEffect, useRef, useState } from 'react';
import { useCarrier } from '../../contexts/CarrierContext';
import { useAuth } from '../../contexts/AuthContext';
import CarrierBadge from '../carrier/CarrierBadge';
import CarrierRulesMissing from '../carrier/CarrierRulesMissing';
import { toFa } from '../../utils/faFormat';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  ArrowRight,
  Camera,
  CheckCircle2,
  ChevronDown,
  Crown,
  Eye,
  FileText,
  HelpCircle,
  Info,
  Loader2,
  Play,
  Scale,
  ScrollText,
  Search,
  Tag,
  Wrench,
  XCircle,
} from 'lucide-react';
import { INSPECTION_CATEGORIES } from '../../lib/inspectionCategories';
import { AI_MODELS, modelsForSurface } from '../../lib/aiModels';
import {
  INSPECTION_SLOT_KEYS,
  getSlotPresentation,
} from '../../lib/inspectionSettings';
import { ModelLogo as ExternalModelLogo } from './ModelLogos';
import EvidenceGallery from './EvidenceGallery';
import ConstellationBanner from './ConstellationBanner';
import { useEvidenceGps } from '../../hooks/useEvidenceGps';
import '../../styles/InspectionTab.css';
import '../../styles/EvidenceUpload.css';

// ─── Council compositions ───────────────────────────────────────────────────
// These records describe layout only. Runtime identity always comes from the
// shared settings bundle, so concealed/loading/error renders have no fallback
// vendor descriptor to leak.
export const COUNCIL_MODELS = [{}, {}, {}];

export const COUNCIL_MODELS_STRIKE = [{}, {}];

export const COUNCIL_MODELS_SOLO = [{}];

// ─── Severity styling ───────────────────────────────────────────────────────
const SEVERITY_CONFIG = {
  critical: {
    label: 'بحرانی',
    icon: AlertOctagon,
    fg: '#b91c1c',
    fgDeep: '#7f1d1d',
    bg: '#fef2f2',
    border: '#fecaca',
  },
  major: {
    label: 'عمده',
    icon: AlertTriangle,
    fg: '#b45309',
    fgDeep: '#78350f',
    bg: '#fffbeb',
    border: '#fed7aa',
  },
  minor: {
    label: 'جزئی',
    icon: Info,
    fg: '#a16207',
    fgDeep: '#713f12',
    bg: '#fefce8',
    border: '#fde68a',
  },
  documentation: {
    label: 'مستندسازی',
    icon: FileText,
    fg: '#0369a1',
    fgDeep: '#0c4a6e',
    bg: '#f0f9ff',
    border: '#bae6fd',
  },
};

const SEVERITY_ORDER = { critical: 0, major: 1, minor: 2, documentation: 3 };

// ─── Status styling (per-finding) ───────────────────────────────────────────
const STATUS_CONFIG = {
  not_ok: {
    label: 'عدم انطباق',
    icon: XCircle,
    fg: '#b91c1c',
  },
  needs_confirmation: {
    label: 'نیاز به تأیید',
    icon: HelpCircle,
    fg: '#a16207',
  },
  not_verifiable: {
    label: 'قابل تأیید نیست',
    icon: Search,
    fg: '#475569',
  },
};

// ─── Overall verdict header styling ─────────────────────────────────────────
const OVERALL_STATUS_CONFIG = {
  compliant: { label: 'منطبق', fg: '#065f46', bg: '#ecfdf5', border: '#a7f3d0', icon: CheckCircle2 },
  non_compliant: { label: 'عدم انطباق', fg: '#7f1d1d', bg: '#fef2f2', border: '#fecaca', icon: AlertOctagon },
  partially_compliant: { label: 'انطباق جزئی', fg: '#78350f', bg: '#fffbeb', border: '#fed7aa', icon: AlertTriangle },
  insufficient_evidence: { label: 'شواهد ناکافی', fg: '#1e293b', bg: '#f1f5f9', border: '#cbd5e1', icon: Eye },
};

const FINAL_ACTION_CONFIG = {
  proceed: { label: 'اجازهٔ ادامهٔ کار', fg: '#065f46', icon: ArrowRight },
  correct_before_proceed: { label: 'پیش از ادامه، رفع نقص', fg: '#7f1d1d', icon: Wrench },
  needs_more_photos: { label: 'نیاز به عکس‌های بیشتر', fg: '#78350f', icon: Camera },
};

const NEUTRAL_MODEL_REFERENCE = 'مدل هوش مصنوعی';
const FLEXIBLE_IDENTITY_SEPARATOR = '[\\s._\u2010-\u2015\u2212-]*';

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function identityPattern(value) {
  return value
    .split(/[\s._\u2010-\u2015\u2212-]+/u)
    .filter(Boolean)
    .map(escapeRegex)
    .join(FLEXIBLE_IDENTITY_SEPARATOR);
}

const MODEL_IDENTITY_PATTERNS = [...new Set(AI_MODELS.flatMap((model) => {
  const [provider, modelSlug = ''] = model.id.split('/');
  const family = modelSlug.split(/[._-]/u)[0];
  const vendorPrefix = model.vendor.split(/\s+/u)[0];
  const labelParts = model.label.split(/[\s._\u2010-\u2015\u2212-]+/u);
  const labelRemainder = labelParts.slice(1).join(' ');
  const recognizableAlias = /[A-Za-z]/u.test(labelRemainder) && /\d/u.test(labelRemainder)
    ? labelRemainder
    : '';
  return [
    model.id,
    model.label,
    model.vendor,
    provider,
    family,
    vendorPrefix,
    recognizableAlias,
  ];
}).filter((value) => value && value.toLocaleLowerCase('en-US') !== 'ai'))]
  .sort((left, right) => right.length - left.length)
  .map((value) => new RegExp(
    `(^|[^A-Za-z0-9])(${identityPattern(value)})(?=$|[^A-Za-z0-9])`,
    'giu',
  ));

function redactModelIdentityString(value) {
  return MODEL_IDENTITY_PATTERNS.reduce(
    (redacted, pattern) => redacted.replace(
      pattern,
      (_match, prefix) => `${prefix}${NEUTRAL_MODEL_REFERENCE}`,
    ),
    value,
  );
}

function redactVerdictModelIdentities(value) {
  if (typeof value === 'string') return redactModelIdentityString(value);
  if (Array.isArray(value)) return value.map(redactVerdictModelIdentities);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, nestedValue]) => (
      [key, redactVerdictModelIdentities(nestedValue)]
    )));
  }
  return value;
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function StatusIcon({ status, size = 18 }) {
  if (status === 'running') {
    return <Loader2 className="status-icon spin" size={size} aria-label="در حال پردازش" />;
  }
  if (status === 'done') {
    return <CheckCircle2 className="status-icon done" size={size} aria-label="تکمیل شد" />;
  }
  if (status === 'error') {
    return <AlertCircle className="status-icon error" size={size} aria-label="خطا" />;
  }
  return <span className="status-icon idle" aria-label="در انتظار" />;
}

function statusLabel(status) {
  switch (status) {
    case 'running': return 'در حال تحلیل…';
    case 'done': return 'تکمیل شد';
    case 'error': return 'خطا در تحلیل';
    default: return 'در انتظار';
  }
}

// ─── Model picker dropdown menu (portaled to body so it escapes .model-card
// ─── which has overflow:hidden for the rounded-corner accent bar clip) ─────
function ModelPickerPortal({ open, anchorRef, onClose, options, selectedId, onSelect }) {
  const [pos, setPos] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open || !anchorRef.current) { setPos(null); return; }
    const computePos = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      // 5 items × ~26px + 8px padding + 2px border = ~140px. Estimate
      // conservatively (200px) so a slightly bigger menu also fits.
      const estHeight = 200;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const placeAbove = spaceBelow < estHeight && spaceAbove > spaceBelow;
      setPos({
        top: placeAbove
          ? Math.max(8, r.top - estHeight - 4)
          : r.bottom + 4,
        right: Math.max(8, window.innerWidth - r.right),
        placeAbove,
      });
    };
    computePos();

    const onDocClick = (e) => {
      // Close only if the click was OUTSIDE both the anchor button AND the
      // portaled menu itself. Without the menuRef check, mousedown on a menu
      // item would fire this handler before the item's own onClick, unmount
      // the menu, and swallow the selection - which is exactly the bug we
      // had where "nothing happens" when you pick a model.
      if (anchorRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    // Defer listeners by a tick so the click that opened the menu doesn't
    // immediately close it.
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
      window.addEventListener('scroll', computePos, true);
      window.addEventListener('resize', computePos);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={`model-picker-menu model-picker-menu-portal${pos.placeAbove ? ' above' : ''}`}
      style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999, maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}
      dir="rtl"
    >
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          className={`model-picker-item${!opt.compatible ? ' disabled' : ''}${opt.id === selectedId ? ' selected' : ''}`}
          disabled={!opt.compatible}
          onClick={() => { onSelect(opt.id); onClose(); }}
          title={opt.compatible ? '' : 'بدون پشتیبانی از تصویر'}
        >
          <ExternalModelLogo logoComponent={opt.logoComponent} size={14} />
          <span>{opt.label}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
}

// ─── Chairman row (stage 3) - model picker variant of the chairman badge ────
function ChairmanRow({
  concealModelIdentities,
  onModelChange,
  presentation,
  stage3Status,
  slotIndex,
  slotKey,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const visible = concealModelIdentities === false && Boolean(presentation.model);
  const options = visible ? modelsForSurface(slotKey) : [];

  return (
    <div className="chairman-row" data-inspection-slot={slotKey}>
      <div className="chairman-badge">
        <Crown size={20} />
        <div className="chairman-identity">
          <div className="chairman-name">
            {visible && presentation.logo ? (
              <ExternalModelLogo logoComponent={presentation.logo} size={20} />
            ) : null}
            {presentation.label}
          </div>
          <div className="chairman-role">رئیس شورا</div>
          {visible && onModelChange ? (
            <div className="model-picker">
              <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)} className="model-picker-btn">
                <ChevronDown size={12} />
                تغییر مدل
              </button>
              <ModelPickerPortal
                open={open}
                anchorRef={btnRef}
                onClose={() => setOpen(false)}
                options={options}
                selectedId={presentation.model.id}
                onSelect={(id) => onModelChange(slotIndex, id)}
              />
            </div>
          ) : <div className="model-picker-space" aria-hidden />}
        </div>
      </div>
      <div className="chairman-status">
        <StatusIcon status={stage3Status} size={14} />
        <span>{statusLabel(stage3Status)}</span>
      </div>
    </div>
  );
}

// ─── Model card ─────────────────────────────────────────────────────────────
function ModelCard({
  concealModelIdentities,
  onModelChange,
  presentation,
  slotIndex,
  slotKey,
  status,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const visible = concealModelIdentities === false && Boolean(presentation.model);
  const options = visible ? modelsForSurface(slotKey) : [];

  return (
    <motion.div
      className="model-card"
      data-inspection-slot={slotKey}
      data-state={status || 'idle'}
      whileHover={{ y: -2 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="model-accent" />
      <div className="model-head">
        <div className="model-logo">
          {visible && presentation.logo ? (
            <ExternalModelLogo logoComponent={presentation.logo} size={28} />
          ) : <span className="model-logo-placeholder" aria-hidden />}
        </div>
      </div>
      <div className="model-name">{presentation.label}</div>
      {visible && onModelChange ? (
        <div className="model-picker">
          <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)} className="model-picker-btn">
            <ChevronDown size={12} />
            تغییر مدل
          </button>
          <ModelPickerPortal
            open={open}
            anchorRef={btnRef}
            onClose={() => setOpen(false)}
            options={options}
            selectedId={presentation.model.id}
            onSelect={(id) => onModelChange(slotIndex, id)}
          />
        </div>
      ) : <div className="model-picker-space" aria-hidden />}
      <div className="model-status">
        <StatusIcon status={status} size={14} />
        <span>{statusLabel(status)}</span>
      </div>
    </motion.div>
  );
}

// ─── Stage block ────────────────────────────────────────────────────────────
function StageBlock({ index, title, subtitle, active, children }) {
  const numeral = ['۱', '۲', '۳'][index];
  return (
    <motion.div
      className={`stage-block${active ? ' active' : ''}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: 0.05 + index * 0.08, ease: 'easeOut' }}
    >
      <div className="stage-head">
        <span className="stage-number" aria-hidden>{numeral}</span>
        <div className="stage-titles">
          <div className="stage-title">{title}</div>
          <div className="stage-subtitle">{subtitle}</div>
        </div>
      </div>
      <div className="stage-body">{children}</div>
    </motion.div>
  );
}

// ─── Violation card (expandable) ────────────────────────────────────────────
function ViolationCard({ violation, index }) {
  const [open, setOpen] = useState(false);
  const cfg = SEVERITY_CONFIG[violation.severity] || SEVERITY_CONFIG.minor;
  const Icon = cfg.icon;
  const statusCfg = violation.status ? STATUS_CONFIG[violation.status] : null;
  const StatusIcon = statusCfg?.icon;
  const confidencePct = Math.round((violation.confidence ?? 0) * 100);
  const hasDetail = Boolean(
    violation.rationale || violation.visual_evidence || violation.corrective_action,
  );

  return (
    <motion.article
      className="violation"
      style={{
        '--vfg': cfg.fg,
        '--vfg-deep': cfg.fgDeep,
        '--vbg': cfg.bg,
        '--vborder': cfg.border,
      }}
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: index * 0.06 }}
      layout
    >
      <header className="violation-head">
        <span className="violation-badge">
          <Icon size={13} />
          {cfg.label}
        </span>
        {statusCfg && (
          <span className="violation-status" style={{ '--sfg': statusCfg.fg }}>
            <StatusIcon size={12} />
            {statusCfg.label}
          </span>
        )}
        <span className="violation-confidence">
          اطمینان {confidencePct}٪
        </span>
      </header>
      {violation.rule && (
        <div className="violation-rule">{violation.rule}</div>
      )}
      <div className="violation-description">
        {violation.description}
      </div>
      {hasDetail && (
        <>
          <button
            type="button"
            className="violation-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            <ChevronDown size={14} className={open ? 'rot' : ''} />
            {open ? 'بستن جزئیات' : 'جزئیات شورا'}
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                className="violation-details"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
              >
                {violation.visual_evidence && (
                  <div className="violation-detail-block">
                    <div className="violation-detail-label">
                      <Eye size={12} /> شواهد بصری
                    </div>
                    <div className="violation-detail-body">{violation.visual_evidence}</div>
                  </div>
                )}
                {violation.rationale && (
                  <div className="violation-detail-block">
                    <div className="violation-detail-label">
                      <Scale size={12} /> استدلال
                    </div>
                    <div className="violation-detail-body">{violation.rationale}</div>
                  </div>
                )}
                {violation.corrective_action && (
                  <div className="violation-detail-block">
                    <div className="violation-detail-label">
                      <Wrench size={12} /> اقدام اصلاحی پیشنهادی
                    </div>
                    <div className="violation-detail-body">{violation.corrective_action}</div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </motion.article>
  );
}

// ─── Verdict header (overall_status + summary + final_action) ───────────────
function VerdictHeader({ verdict }) {
  if (!verdict) return null;
  const overall = OVERALL_STATUS_CONFIG[verdict.overall_status] || null;
  const action = FINAL_ACTION_CONFIG[verdict.final_action] || null;
  if (!overall && !action && !verdict.summary) return null;
  const OverallIcon = overall?.icon;
  const ActionIcon = action?.icon;
  return (
    <motion.div
      className="verdict-header"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {overall && (
        <div
          className="verdict-overall"
          style={{ '--ofg': overall.fg, '--obg': overall.bg, '--obd': overall.border }}
        >
          <OverallIcon size={16} />
          <span>{overall.label}</span>
        </div>
      )}
      {verdict.summary && (
        <div className="verdict-summary">{verdict.summary}</div>
      )}
      {action && (
        <div className="verdict-final-action" style={{ '--afg': action.fg }}>
          <ActionIcon size={14} />
          <span>{action.label}</span>
        </div>
      )}
    </motion.div>
  );
}

// ─── Verdict empty state ────────────────────────────────────────────────────
function VerdictEmpty({ hasImage, isSolo }) {
  return (
    <div className="verdict-empty">
      <div className="empty-icon" aria-hidden>
        <ScrollText size={36} strokeWidth={1.4} />
      </div>
      <div className="empty-title">هنوز تحلیلی انجام نشده</div>
      <div className="empty-hint">
        {hasImage
          ? (isSolo
            ? 'برای دیدن نتیجه، روی «شروع تحلیل» کلیک کنید'
            : 'برای دیدن رأی شورا، روی «شروع تحلیل شورا» کلیک کنید')
          : 'لطفاً ابتدا تصویری بارگذاری کنید'}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
//
// Two variants ride the same component (Phase: شورای ضربت):
//
//   councilModels: array of neutral member-shell records. Defaults to the
//     full 3-model COUNCIL_MODELS. The strike variant passes
//     COUNCIL_MODELS_STRIKE (2 entries, no peer-review). Stage 1 renders one
//     ModelCard per entry - the layout grid scales naturally.
//
//   showStage2 - false hides the entire "ارزیابی متقابل" block. The strike
//     variant has only two stages (parallel → chairman), so we re-label the
//     chairman block as "stage 2" via stageIndex.
//
//   stageStatus: the same { stage1: {slotKey: status}, stage2: status,
//     stage3: status } shape regardless of variant. The strike hook
//     populates stage2='idle' and ignores it.
export default function InspectionTab({
  entries = [],
  uploadProgress = [],
  onImagesAdd,
  onImageRemove,
  onImagesClear,
  onAnalyze,
  isProcessing = false,
  stageStatus = { stage1: {}, stage2: 'idle', stage3: 'idle' },
  stage1Results = null,
  stage2Rankings = null,
  finalVerdict = null,
  runMeta = null,
  error = null,
  categoryId = '',
  onCategoryChange = () => {},
  availableCategoryIds = [],
  categoriesStatus = 'loading',
  categoriesError = null,
  refreshAvailableCategories = () => {},
  councilModels = COUNCIL_MODELS,
  showStage2 = true,
  showStage3 = true,
  slotKeys = [],
  effectiveModels = [],
  concealModelIdentities = true,
  onModelChange = () => {},
}) {
  const { carrier } = useCarrier();
  const { profile } = useAuth();
  const canEdit = ['admin', 'super_admin'].includes(profile?.role);
  void stage1Results; void stage2Rankings;
  void onImagesClear;

  const selectedCategory = INSPECTION_CATEGORIES.find((c) => c.id === categoryId) || null;
  // Loading and failed discovery both keep the picker closed. Only a
  // successful empty response means this carrier has no published rules.
  const availableSet = new Set(availableCategoryIds || []);
  const categoriesReady = categoriesStatus === 'ready';
  const isCategoryAvailable = (id) => categoriesReady && availableSet.has(id);
  const allDisabled = categoriesReady && availableSet.size === 0;
  const { coords, extractingIndices, validation } = useEvidenceGps(entries);
  const gpsOk = entries.length === 0 || validation.kind === 'ok';
  const offenderIndices = validation.offenderIndices || null;
  const canAnalyze = entries.length > 0 && Boolean(categoryId) && isCategoryAvailable(categoryId) && !isProcessing && gpsOk;
  const isSolo = councilModels.length === 1;
  const stage1Active = Object.values(stageStatus.stage1 || {}).some(
    (s) => s && s !== 'idle',
  );
  const stage2Active = stageStatus.stage2 && stageStatus.stage2 !== 'idle';
  const stage3Active = stageStatus.stage3 && stageStatus.stage3 !== 'idle';

  // When stage2 is hidden, the chairman becomes stage 2 in the numbering.
  // StageBlock takes a numeric index 0..N-1 and shows the corresponding
  // Persian numeral ['۱','۲','۳']. Recompute indices per variant.
  const stage1Index    = 0;
  const stage2Index    = 1;
  const chairmanIndex  = showStage2 ? 2 : 1;
  const fallbackSlotKeys = showStage3
    ? (showStage2 ? INSPECTION_SLOT_KEYS.full : INSPECTION_SLOT_KEYS.strike)
    : INSPECTION_SLOT_KEYS.solo;
  const semanticSlotKeys = Array.isArray(slotKeys) && slotKeys.length === fallbackSlotKeys.length
    ? slotKeys
    : fallbackSlotKeys;
  const presentationFor = (slotIndex) => {
    const presentation = getSlotPresentation({
      conceal: concealModelIdentities,
      slotIndex,
      modelId: effectiveModels?.[slotIndex],
    });
    return presentation.label
      ? presentation
      : getSlotPresentation({ conceal: true, slotIndex });
  };
  const memberPresentations = councilModels.map((_model, slotIndex) => presentationFor(slotIndex));
  const chairmanSlotIndex = councilModels.length;
  const chairmanPresentation = showStage3 ? presentationFor(chairmanSlotIndex) : null;
  const identitiesVisible = concealModelIdentities === false;
  const displayedError = error && !identitiesVisible
    ? 'تحلیل با خطا متوقف شد. لطفاً دوباره تلاش کنید.'
    : error;
  const displayedVerdict = identitiesVisible
    ? finalVerdict
    : redactVerdictModelIdentities(finalVerdict);

  const sortedViolations = displayedVerdict?.violations
    ? [...displayedVerdict.violations].sort(
        (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9),
      )
    : null;

  return (
    <div className="inspection-root" dir="rtl">
      <AnimatePresence>
        {error && (
          <motion.div
            className="inspection-error"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            role="alert"
          >
            <AlertCircle size={18} />
            <span>{displayedError}</span>
            {entries.length > 0 && (
              <button
                type="button"
                className="error-retry"
                onClick={() => onAnalyze(coords.filter(Boolean))}
              >
                تلاش دوباره
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="inspection-grid">

        {/* ─── LEFT: Image upload ─────────────────────────────────── */}
        <section className="col col-upload" aria-label="بارگذاری تصویر">
          <header className="col-header">
            <Camera size={18} />
            <h3>تصویر بازرسی</h3>
          </header>
          <div className="col-body">
            <div className="category-block">
              <label htmlFor="inspection-category" className="category-label">
                <Tag size={14} />
                <span>نوع بازرسی</span>
                <span className="category-required" aria-hidden>*</span>
              </label>
              <div className="category-select-wrap">
                <select
                  id="inspection-category"
                  className="category-select"
                  value={categoryId}
                  onChange={(e) => onCategoryChange(e.target.value)}
                  disabled={isProcessing || !categoriesReady}
                >
                  <option value="">- انتخاب کنید -</option>
                  {INSPECTION_CATEGORIES.map((c) => {
                    const available = isCategoryAvailable(c.id);
                    return (
                      <option
                        key={c.id}
                        value={c.id}
                        disabled={!available}
                      >
                        {c.label_fa}{!available ? '  (قوانین وارد نشده)' : ''}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown size={14} className="category-caret" aria-hidden />
              </div>
              {selectedCategory && (
                <div className="category-help">{selectedCategory.description_fa}</div>
              )}
              {categoriesStatus === 'loading' && (
                <p role="status" style={{ color: '#475569' }}>دریافت دسته‌های بازرسی در حال انجام است.</p>
              )}
              {categoriesStatus === 'error' && (
                <div className="inspection-error" role="alert">
                  <AlertCircle size={18} />
                  <span>{categoriesError || 'دریافت دسته‌های بازرسی ناموفق بود. دوباره تلاش کنید.'}</span>
                  <button type="button" className="error-retry" onClick={refreshAvailableCategories}>
                    تلاش دوباره
                  </button>
                </div>
              )}
              {allDisabled && (
                <CarrierRulesMissing domain="supervision" carrier={carrier} canEdit={canEdit} />
              )}
            </div>

            <div className="category-divider" />

            {/* Gate the upload UI behind category selection. Until the user
                has picked a type, the EvidenceGallery is replaced by a
                quiet hint - both visually obvious AND functionally inert
                (no file input, no drop handlers, no event listeners) so
                an accidental drag-drop on this column never triggers any
                upload path. */}
            {!categoryId ? (
              <div className="upload-gate" aria-live="polite">
                <ScrollText size={26} strokeWidth={1.4} />
                <div className="upload-gate-primary">ابتدا نوع بازرسی را انتخاب کنید</div>
                <div className="upload-gate-secondary">
                  پس از انتخاب نوع، امکان بارگذاری تصویر فعال می‌شود.
                </div>
              </div>
            ) : (
              <EvidenceGallery
                entries={entries}
                coords={coords}
                uploadProgress={uploadProgress}
                onAdd={onImagesAdd}
                onRemove={onImageRemove}
                disabled={isProcessing}
                extractingIndices={extractingIndices}
                offenderIndices={offenderIndices}
              />
            )}
            {entries.length > 0 && (
              <ConstellationBanner coords={coords} validation={validation} />
            )}
          </div>
        </section>

        {/* ─── CENTER: Council process ────────────────────────────── */}
        <section className="col col-council" aria-label={isSolo ? 'تحلیل مستقل' : 'شورای تحلیل'}>
          <header className="col-header">
            <Scale size={18} />
            <h3>{isSolo ? 'تحلیل مستقل' : 'شورای تحلیل'}</h3>
          </header>

          <div className="col-body">
            <motion.button
              type="button"
              className="analyze-btn"
              disabled={!canAnalyze}
              onClick={() => onAnalyze(coords.filter(Boolean))}
              whileTap={canAnalyze ? { scale: 0.985 } : undefined}
            >
              {isProcessing ? (
                <>
                  <Loader2 size={20} className="spin" />
                  {isSolo ? 'در حال تحلیل…' : 'در حال تحلیل توسط شورا…'}
                </>
              ) : (
                <>
                  <Play size={18} fill="currentColor" />
                  {isSolo ? 'شروع تحلیل' : 'شروع تحلیل شورا'}
                </>
              )}
            </motion.button>

            {!isProcessing && (!entries.length || !categoryId || !gpsOk) && (
              <div className="analyze-hint">
                <Info size={13} />
                <span>
                  {!categoryId && !entries.length && 'ابتدا نوع بازرسی را انتخاب و عکس‌ها را بارگذاری کنید'}
                  {!categoryId && entries.length > 0 && 'نوع بازرسی را انتخاب کنید'}
                  {categoryId && !entries.length && 'حداقل یک عکس بازرسی بارگذاری کنید'}
                  {categoryId && entries.length > 0 && !gpsOk && 'پیش از تحلیل، مشکل موقعیت جغرافیایی عکس‌ها را برطرف کنید'}
                </span>
              </div>
            )}

            <div className="stages">
              <StageBlock
                index={stage1Index}
                title={councilModels.length === 1 ? 'تحلیل تخصصی' : 'تحلیل موازی'}
                subtitle={
                  councilModels.length === 1
                    ? (identitiesVisible && memberPresentations[0].model
                      ? `${memberPresentations[0].label} با عمق تفکر تنظیم‌شده، تصویر و قوانین را در یک گام تحلیل می‌کند`
                      : 'بازرس مستقل، تصویر و قوانین را در یک گام تحلیل می‌کند')
                    : councilModels.length === 2
                      ? 'هر دو مدل به‌صورت مستقل تصویر و قوانین را بررسی می‌کنند'
                      : 'هر سه مدل به‌صورت مستقل تصویر و قوانین را بررسی می‌کنند'
                }
                active={stage1Active}
              >
                <div className="model-grid">
                  {councilModels.map((_member, i) => (
                    <ModelCard
                      key={semanticSlotKeys[i]}
                      concealModelIdentities={concealModelIdentities}
                      onModelChange={onModelChange}
                      presentation={memberPresentations[i]}
                      slotIndex={i}
                      slotKey={semanticSlotKeys[i]}
                      status={stageStatus.stage1?.[semanticSlotKeys[i]] || 'idle'}
                    />
                  ))}
                </div>
              </StageBlock>

              {showStage2 && (
                <StageBlock
                  index={stage2Index}
                  title="ارزیابی متقابل"
                  subtitle="هر مدل پاسخ‌های ناشناس دو مدل دیگر را رتبه‌بندی می‌کند"
                  active={stage2Active}
                >
                  <div className="peer-review">
                    <div className="peer-graphic" aria-hidden>
                      <span className="peer-node">A</span>
                      <span className="peer-arrow">⇄</span>
                      <span className="peer-node">B</span>
                      <span className="peer-arrow">⇄</span>
                      <span className="peer-node">C</span>
                    </div>
                    <div className="peer-status">
                      <StatusIcon status={stageStatus.stage2} size={14} />
                      <span>{statusLabel(stageStatus.stage2)}</span>
                    </div>
                  </div>
                </StageBlock>
              )}

              {showStage3 && (
                <StageBlock
                  index={chairmanIndex}
                  title="رأی رئیس شورا"
                  subtitle={showStage2
                    ? (identitiesVisible && chairmanPresentation.model
                      ? `جمع‌بندی نهایی و صدور رأی توسط ${chairmanPresentation.label}`
                      : 'جمع‌بندی نهایی و صدور رأی توسط رئیس شورا')
                    : (identitiesVisible && chairmanPresentation.model
                      ? `${chairmanPresentation.label} با عمق تفکر تنظیم‌شده، تحلیل دو مدل دیگر را می‌خواند و رأی نهایی را با دقت بالا صادر می‌کند`
                      : 'رئیس شورا تحلیل دو مدل دیگر را می‌خواند و رأی نهایی را صادر می‌کند')}
                  active={stage3Active}
                >
                  <ChairmanRow
                    concealModelIdentities={concealModelIdentities}
                    onModelChange={onModelChange}
                    presentation={chairmanPresentation}
                    stage3Status={stageStatus.stage3}
                    slotIndex={chairmanSlotIndex}
                    slotKey={semanticSlotKeys[chairmanSlotIndex]}
                  />
                </StageBlock>
              )}
            </div>
          </div>
        </section>

        {/* ─── RIGHT: Final verdict ───────────────────────────────── */}
        <section className="col col-verdict" aria-label={isSolo ? 'نتیجهٔ نهایی تحلیل' : 'رأی نهایی شورا'}>
          <header className="col-header">
            <ScrollText size={18} />
            <h3>رأی نهایی</h3>
            {sortedViolations && (
              <span className="verdict-count">
                {sortedViolations.length} مورد
              </span>
            )}
          </header>
          <div className="col-body">
            {finalVerdict && runMeta && <div style={{ color: 'var(--admin-text-muted, #475569)', marginBottom: 12 }}>
              <CarrierBadge carrier={runMeta.carrier} />{' · '}
              <span>{runMeta.rulebook_version_no == null ? 'پیش از نسخه‌بندی قواعد' : `قواعد نسخهٔ ${toFa(runMeta.rulebook_version_no)}`}</span>
            </div>}
            {!sortedViolations ? (
              <VerdictEmpty hasImage={entries.length > 0} isSolo={isSolo} />
            ) : sortedViolations.length === 0 ? (
              <>
                <VerdictHeader verdict={displayedVerdict} />
                <motion.div
                  className="verdict-clean"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <CheckCircle2 size={40} strokeWidth={1.5} />
                  <div className="clean-title">هیچ مشکلی شناسایی نشد</div>
                  <div className="clean-hint">
                    نصب طبق دستورالعمل‌ها انجام شده است
                  </div>
                </motion.div>
              </>
            ) : (
              <>
                <VerdictHeader verdict={displayedVerdict} />
                <div className="violations">
                  <AnimatePresence>
                    {sortedViolations.map((v, i) => (
                      <ViolationCard
                        key={`${v.rule || 'rule'}-${i}`}
                        violation={v}
                        index={i}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
